import { JsonPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input, signal } from "@angular/core";
import { AssessmentTypeNames, AssessmentTypes } from "src/app/core/models/assessment-types.model";
import { ScoreCutoffs } from "src/app/core/models/score-cutoffs.model";
import { getNumericValueOfGrade } from "src/app/core/utils/grade";
import { StudentDisplay } from "src/app/subscriptions/manage/manage-subscription";
import { first } from "underscore";

interface ResultColumn {
  gradeHeader: string;
  scoreRanges: {
    proficient: string;
    some: string;
    high: string;
  },
  counts: {
    proficient: number;
    some: number;
    high: number;
  }
}

@Component({
  selector: 'wf-screener-results',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: 'screener-results.component.css',
  template: `
    <div class="screener-results-container">
      <table class="screener-results-table">
        <colgroup>
          <col/>
          <col/>
          <col/>
        </colgroup>
        <thead>
          <tr>
            <th></th>
            @for (column of resultColumns(); track column.gradeHeader) {
              <th>Grade: {{ column.gradeHeader }}</th>
            }
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">
              <div class="legend-key legend-green">
                <div class="pie-legend">
                  Proficient
                </div>
                <img class="legend-img" src="/assets/images/reportIcons/iconScoreGreen.svg" />
              </div>
            </th>
            @for (column of resultColumns(); track column.gradeHeader) {
              <td>
                <div class="screener-result screener-result-green">
                  <span>{{ column.scoreRanges.proficient }}</span>
                  <span>{{ column.counts.proficient }}</span>
                </div>
              </td>
            }
          </tr>
          <tr>
            <th scope="row" class="recommended-category-cell">
              <div class="legend-key legend-yellow">
                <div class="pie-legend">
                  Some Risk
                </div>
                <img class="legend-img" src="/assets/images/reportIcons/iconScoreYellow.svg" />
              </div>
            </th>
            @for (column of resultColumns(); track column.gradeHeader) {
              <td class="recommended-category-cell">
                <div class="screener-result screener-result-yellow">
                  <span>{{ column.scoreRanges.some }}</span>
                  <span>{{ column.counts.some }}</span>
                </div>
              </td>
            }
          </tr>
          <tr>
            <th scope="row" class="recommended-category-cell">
              <div class="legend-key legend-red">
                <div class="pie-legend">
                  High Risk
                </div>
                <img class="legend-img" src="/assets/images/reportIcons/iconScoreRed.svg" />
              </div>
            </th>
            @for (column of resultColumns(); track column.gradeHeader) {
              <td class="recommended-category-cell">
                <div class="screener-result screener-result-red">
                  <span>{{ column.scoreRanges.high }}</span>
                  <span>{{ column.counts.high }}</span>
                </div>
              </td>
            }
          </tr>
        </tbody>
      </table>
      <span class="recommended-text">Recommended for Wordflight</span>
    </div>
  `
})
export class ScreenerResultsComponent {
  readonly grade = input.required<string>();
  readonly students = input.required<StudentDisplay[]>();

  readonly resultColumns = computed(() => {
    // grade is actually 1 number larger than name (i.e. 7th grade is value 8)
    const grade = this.grade();
    const students = this.students();

    const columns: ResultColumn[] = [];
    if (!grade || grade === 'all' || grade === 'four_below_all') {
      const fourthAndBelowStudents = students.filter(s => s.grade <= 5);
      columns.push({
        gradeHeader: 'All 4th & Below',
        scoreRanges: {
          proficient: ScoreCutoffs['elementary']['labels']['topTier']['range'],
          some: ScoreCutoffs['elementary']['labels']['middleTier']['range'],
          high: ScoreCutoffs['elementary']['labels']['bottomTier']['range'],
        },
        counts: this.countsForZones(fourthAndBelowStudents, ScoreCutoffs['elementary']['zones'])
      });
    }
    if (!grade || grade === 'all' || grade === 'five_up_all') {
      const fifthAndAboveStudents = students.filter(s => s.grade >= 6);
      columns.push({
        gradeHeader: 'All 5th & Above',
        scoreRanges: {
          proficient: ScoreCutoffs['secondary']['labels']['topTier']['range'],
          some: ScoreCutoffs['secondary']['labels']['middleTier']['range'],
          high: ScoreCutoffs['secondary']['labels']['bottomTier']['range'],
        },
        counts: this.countsForZones(fifthAndAboveStudents, ScoreCutoffs['secondary']['zones'])
      });
    }
    if (grade && !grade.includes("all")) {
      const numericGrade = getNumericValueOfGrade(grade);
      console.log('numericGrade', numericGrade);
      const singleGradeStudents = students.filter(s => (s.grade - 1) === numericGrade);
      const category = numericGrade <= 4 ? 'elementary' : 'secondary';

      columns.push({
        gradeHeader: grade,
        scoreRanges: {
          proficient: ScoreCutoffs[category]['labels']['topTier']['range'],
          some: ScoreCutoffs[category]['labels']['middleTier']['range'],
          high: ScoreCutoffs[category]['labels']['bottomTier']['range'],
        },
        counts: this.countsForZones(singleGradeStudents, ScoreCutoffs[category]['zones'])
      })
    }

    return columns;
  });

  private countStudentsInRange(students: StudentDisplay[], bottom: number, top: number) {
    return students
      .filter(s => {
        const firstAssessmentScore = s.firstAssessmentScore
        if (!firstAssessmentScore
          || firstAssessmentScore.assessmentType !== AssessmentTypeNames.SCREENER
          || !firstAssessmentScore.dateCompleted
        ) {
          return false;
        }
        // if bottom === 0, then then assume no lower bound, as some systemScore can be negative
        return (bottom === 0 || bottom <= firstAssessmentScore.systemScore) && firstAssessmentScore.systemScore <= top;
      })
      .length;
  }

  private countsForZones(students: StudentDisplay[], zones: any[]) {
    return  {
      // high risk Zones 1 - 2
      high: this.countStudentsInRange(students, zones[0].bottom, zones[1].top),
      // some risk Zones 3
      some: this.countStudentsInRange(students, zones[2].bottom, zones[2].top),
      // proficient Zones 4
      proficient: this.countStudentsInRange(students, zones[3].bottom, zones[3].top),
    };
  }
}
